'use client';

import { MouseEvent } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';

import { Avatar } from '@/components';

interface DesktopOutcomeRowProps {
  name: string;
  image?: string;
  formattedVolume: string;
  percent: string;
  outcomeNameYes: string;
  yesPrice: string;
  outcomeNameNo: string;
  noPrice: string;
  isSelected: boolean;
  selectedSide?: 'yes' | 'no';
  onExpand: () => void;
  onBuyYes: (e: MouseEvent) => void;
  onBuyNo: (e: MouseEvent) => void;
}

export function DesktopOutcomeRow({
  name,
  image,
  formattedVolume,
  percent,
  outcomeNameYes,
  yesPrice,
  outcomeNameNo,
  noPrice,
  isSelected,
  selectedSide,
  onExpand,
  onBuyYes,
  onBuyNo,
}: DesktopOutcomeRowProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const isYesSelected = isSelected && selectedSide === 'yes';
  const isNoSelected = isSelected && selectedSide === 'no';

  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 3,
        py: 2.5,
        px: 2.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: isSelected
          ? isDarkMode
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.04)'
          : 'transparent',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        position: 'relative',
        transform: isSelected ? 'translateY(-2px)' : 'none',
        boxShadow: isSelected
          ? isDarkMode
            ? '0 4px 20px rgba(0,0,0,0.5)'
            : '0 4px 20px rgba(0,0,0,0.08)'
          : 'none',
        zIndex: isSelected ? 2 : 1,
        '&:hover': {
          bgcolor: isSelected
            ? isDarkMode
              ? 'rgba(255, 255, 255, 0.08)'
              : 'rgba(0, 0, 0, 0.06)'
            : 'background.subtle',
          transform: isSelected ? 'translateY(-3px)' : 'none',
          boxShadow: isSelected
            ? isDarkMode
              ? '0 6px 24px rgba(0,0,0,0.6)'
              : '0 6px 24px rgba(0,0,0,0.12)'
            : 'none',
        },
        '&:last-child': {
          borderBottom: 'none',
        },
        '&::before': isSelected
          ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '4px',
              bgcolor: 'primary.main',
            }
          : undefined,
      }}
      onClick={onExpand}
    >
      {/* Left Section - Avatar, Name, and Volume */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 2,
          minWidth: 200,
          flex: 1,
        }}
      >
        {/* Avatar / Image */}
        <Avatar src={image} alt={name || 'Outcome'} size={48} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* Name */}
          <Typography
            fontWeight={600}
            sx={{
              fontSize: '1rem',
              color: 'text.primary',
              lineHeight: 1.2,
            }}
          >
            {name}
          </Typography>

          <Typography
            sx={{
              fontSize: '0.875rem',
              color: 'text.secondary',
              fontWeight: 400,
            }}
          >
            {formattedVolume} Vol.
          </Typography>
        </Box>
      </Box>

      {/* Middle Section - Percentage */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          minWidth: 100,
          flex: 1,
          pr: 4,
        }}
      >
        <Typography
          fontWeight={700}
          sx={{
            fontSize: '1.5rem',
            color: 'text.primary',
            lineHeight: 1,
          }}
        >
          {percent}%
        </Typography>
      </Box>

      {/* Right Section - Buy/Sell Buttons */}
      <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, minWidth: 260 }}>
        <Button
          size="medium"
          variant="contained"
          sx={{
            minWidth: 120,
            fontSize: '0.875rem',
            fontWeight: 600,
            py: 1,
            px: 1.5,
            borderRadius: '8px',
            textTransform: 'none',
            bgcolor: isYesSelected ? 'success.main' : 'success.bg',
            color: isYesSelected ? '#ffffff' : 'success.dark',
            border: 'none',
            boxShadow: isYesSelected ? '0 4px 12px rgba(34, 197, 94, 0.4)' : 'none',
            transform: isYesSelected ? 'translateY(-2px)' : 'none',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              bgcolor: 'success.main',
              color: 'white',
              boxShadow: isYesSelected ? '0 6px 16px rgba(34, 197, 94, 0.5)' : 'none',
              transform: isYesSelected ? 'translateY(-3px)' : 'none',
            },
          }}
          onClick={onBuyYes}
        >
          {outcomeNameYes} {yesPrice}¢
        </Button>
        <Button
          size="medium"
          variant="contained"
          sx={{
            minWidth: 120,
            fontSize: '0.875rem',
            fontWeight: 600,
            py: 1,
            px: 1.5,
            borderRadius: '8px',
            textTransform: 'none',
            bgcolor: isNoSelected ? 'error.main' : 'error.bg',
            color: isNoSelected ? '#ffffff' : 'error.dark',
            border: 'none',
            boxShadow: isNoSelected ? '0 4px 12px rgba(239, 68, 68, 0.4)' : 'none',
            transform: isNoSelected ? 'translateY(-2px)' : 'none',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              bgcolor: 'error.main',
              color: 'white',
              boxShadow: isNoSelected ? '0 6px 16px rgba(239, 68, 68, 0.5)' : 'none',
              transform: isNoSelected ? 'translateY(-3px)' : 'none',
            },
          }}
          onClick={onBuyNo}
        >
          {outcomeNameNo} {noPrice}¢
        </Button>
      </Box>
    </Box>
  );
}
